import { useState } from 'react';
import { Table, Box, Text, IconButton } from '@nimbus-ds/components';
import { TrashIcon } from '@nimbus-ds/icons';
import { InputNumberNimbus } from 'commons/components';
import { HiddenVariantTag } from 'domains/Catalog/Categories/components';
import { useTranslationCatalog } from 'domains/Catalog/hooks';
import { ApplyValuesToAllVariants } from 'domains/Catalog/Products/pages/components';
import {
  extractTransferProduct,
  TransferProductVariant,
} from 'domains/Catalog/Transfers/utils';
import {
  TransferHeaderCells,
  TransferProductCell,
  TransferVariantCell,
} from './components';

interface TransferTableProps {
  productVariants: TransferProductVariant[];
  showZeroQuantityAsWarning: boolean;
  onTransferQuantityChange: (
    productId: string,
    variantId: string,
    quantity: number,
  ) => void;
  onRemoveProduct: (productId: string, variantId: string) => void;
  onApplyValuesToAllVariants: (variantId: string) => void;
  onTransferTotalStock: (productId: string) => void;
}

function TransferTable({
  productVariants,
  showZeroQuantityAsWarning,
  onTransferQuantityChange,
  onRemoveProduct,
  onApplyValuesToAllVariants,
  onTransferTotalStock,
}: Readonly<TransferTableProps>) {
  const t = useTranslationCatalog();

  const [showApplyValuesToAllVariants, setShowApplyValuesToAllVariants] =
    useState<Record<string, boolean>>({});

  const [variantChangedId, setVariantChangedId] = useState<
    undefined | string
  >();

  const selectedProducts = extractTransferProduct(productVariants);

  const handleInputChange =
    (variant: TransferProductVariant) =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setVariantChangedId(variant.variantId);
      const quantity = parseInt(e.target.value) || 0;
      onTransferQuantityChange(variant.productId, variant.variantId, quantity);
    };

  const handleApplyValuesChange = (variant: TransferProductVariant) => () => {
    setVariantChangedId(undefined);
    onApplyValuesToAllVariants(variant.variantId);
  };

  const handleVisibilityChange =
    (variant: TransferProductVariant) => (show: boolean) => {
      setShowApplyValuesToAllVariants((prev) => ({
        ...prev,
        [variant.variantId]: show,
      }));
    };

  const handleRemoveProduct = (variant: TransferProductVariant) => () => {
    onRemoveProduct(variant.productId, variant.variantId);
  };

  const renderTransferCell = (
    variant: TransferProductVariant,
    totalVariants: number,
  ) => (
    <Box display="flex" gap="2" flexDirection="column">
      <InputNumberNimbus
        type="integer"
        aria-label={variant.variantId}
        value={variant.quantity ?? 0}
        appearance={getInputAppearance(variant, showZeroQuantityAsWarning)}
        onChange={handleInputChange(variant)}
      />
      {totalVariants > 1 && (
        <ApplyValuesToAllVariants
          value={variant.quantity}
          valueWasManuallyChanged={variantChangedId === variant.variantId}
          onChange={handleApplyValuesChange(variant)}
          onChangeVisibility={handleVisibilityChange(variant)}
        />
      )}
    </Box>
  );

  const renderExcludeCell = (variant: TransferProductVariant) => (
    <IconButton
      source={<TrashIcon size={16} />}
      size="2rem"
      onClick={handleRemoveProduct(variant)}
      aria-label={`${t('transfers.new.table.headers.exclude')} ${
        variant.variantId
      }`}
    />
  );

  if (productVariants.length === 0) {
    return null;
  }

  return (
    <Table>
      <Table.Head>
        <Table.Row>
          <TransferHeaderCells />
        </Table.Row>
      </Table.Head>
      <Table.Body>
        {selectedProducts.map((product) => (
          <Table.Row key={`${product.productId}`}>
            <Table.Cell>
              <TransferProductCell
                product={product}
                onTransferTotalStock={onTransferTotalStock}
              />
            </Table.Cell>
            <TransferVariantCell
              productId={product.productId}
              productVariants={productVariants}
              showApplyValuesToAllVariants={showApplyValuesToAllVariants}
              renderProp={(variant: TransferProductVariant) => (
                <Box display="flex" flexDirection="row" gap="2">
                  <Text>{formatVariationText(variant)}</Text>
                  <HiddenVariantTag visible={variant.variantVisible} />
                </Box>
              )}
            />
            <TransferVariantCell
              productId={product.productId}
              productVariants={productVariants}
              showApplyValuesToAllVariants={showApplyValuesToAllVariants}
              renderProp={renderStockCell}
            />
            <TransferVariantCell
              productId={product.productId}
              productVariants={productVariants}
              showApplyValuesToAllVariants={showApplyValuesToAllVariants}
              renderProp={renderTransferCell}
            />
            <TransferVariantCell
              productId={product.productId}
              productVariants={productVariants}
              showApplyValuesToAllVariants={showApplyValuesToAllVariants}
              renderProp={renderExcludeCell}
            />
          </Table.Row>
        ))}
      </Table.Body>
    </Table>
  );
}

const formatVariationText = (variant: TransferProductVariant) => {
  const values = Object.values(variant.variantValues);
  return values.join(' / ');
};

const getInputAppearance = (
  variant: TransferProductVariant,
  showZeroQuantityAsWarning: boolean,
) => {
  const variantStock = variant.stock ?? 0;
  const variantQuantity = variant.quantity ?? 0;
  if (variantStock - variantQuantity < 0) return 'danger';
  if (showZeroQuantityAsWarning && variantQuantity === 0) return 'warning';
  return 'neutral';
};

const renderStockCell = (variant: TransferProductVariant) => {
  const nextStock = (variant.stock ?? 0) - (variant.quantity ?? 0);
  const hasQuantity = (variant.quantity ?? 0) > 0;
  return (
    <Box display="flex" gap="2">
      <Text
        textDecoration={hasQuantity ? 'line-through' : 'none'}
        color="neutral-textHigh"
      >
        {variant.stock}
      </Text>
      {!!hasQuantity && <Text color="neutral-textLow">{nextStock}</Text>}
    </Box>
  );
};

export default TransferTable;
